/**
 * tenant-view-store.ts — module-level singleton bridge between the
 * React PlatformTenantContext and the apiJson HTTP client.
 *
 * The HTTP client (api/client.ts) cannot use React hooks. This store
 * provides a getter that the client reads synchronously on every
 * request to attach the `target-tenant-id` header when a platform
 * admin has a tenant selected.
 *
 * Security notes:
 *   - This is a UI convenience bridge ONLY. The backend validates the
 *     target-tenant-id header against the caller's JWT on EVERY request.
 *     A non-platform-admin who somehow sets this store will receive a
 *     backend 403. The frontend gate is belt-and-suspenders only.
 *   - The value is session-scoped (cleared on reload; never written to
 *     localStorage, sessionStorage, or cookies). See context.tsx for the
 *     full security note on why persistent storage is avoided.
 *   - Only a string UUID (or null) is stored — no PII, no tokens.
 *
 * Usage:
 *   // Writer (called by PlatformTenantProvider on every render):
 *   import { setActiveTenantId } from '@/api/tenant-view-store'
 *   setActiveTenantId(selectedTenant?.public_id ?? null)
 *
 *   // Reader (called by api() before every request):
 *   import { getActiveTenantId } from '@/api/tenant-view-store'
 *   const tenantId = getActiveTenantId()
 */

let _activeTenantId: string | null = null

/**
 * Called by PlatformTenantProvider on every state change so the store
 * always reflects the current selected tenant.
 */
export function setActiveTenantId(id: string | null): void {
  _activeTenantId = id
}

/**
 * Returns the public_id of the currently selected tenant, or null if
 * none is selected (or if the provider has not mounted).
 */
export function getActiveTenantId(): string | null {
  return _activeTenantId
}
